import { IUser } from '../user.interface';
import {
  CoverageComponentTypeValues,
  CoverageLimitBehaviorOnExceedValues,
  CoverageLimitMetricValues,
  CoverageLimitPeriodValues,
  CoverageLimitScopeValues,
  SponsorshipActivationRuleTimezoneValues,
  SponsorshipScheduleDayValues,
  CoverageTypeValues,
  CoverageUnitValues,
} from './sponsorship';

export type IJsonValue = string | number | boolean | null | IJsonValue[] | { [key: string]: IJsonValue };
export type IJsonObject = { [key: string]: IJsonValue };

export interface IEnergyWalletCompanySummary {
  id?: number;
  legalName?: string;
  brandName?: string;
  companyType?: string;
  country?: string;
  isActive?: boolean;
  vatNumber?: string;
}

export const EnergyWalletStatuses = {
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  CLOSED: 'closed',
} as const;
export type EnergyWalletStatusValues = typeof EnergyWalletStatuses[keyof typeof EnergyWalletStatuses];

export const EnergyWalletPolicyStatuses = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  DRAFT: 'draft',
} as const;
export type EnergyWalletPolicyStatusValues = typeof EnergyWalletPolicyStatuses[keyof typeof EnergyWalletPolicyStatuses];

export const EnergyWalletGrantModes = {
  MANUAL: 'manual',
  CONTRACT: 'contract',
  RECURRING: 'recurring',
  PURCHASE: 'purchase',
} as const;
export type EnergyWalletGrantModeValues = typeof EnergyWalletGrantModes[keyof typeof EnergyWalletGrantModes];

export const EnergyWalletPeriodTypes = {
  NONE: 'none',
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
} as const;
export type EnergyWalletPeriodTypeValues = typeof EnergyWalletPeriodTypes[keyof typeof EnergyWalletPeriodTypes];

export const EnergyWalletResetModes = {
  ACCUMULATE: 'accumulate',
  RESET: 'reset',
  EXPIRE: 'expire',
} as const;
export type EnergyWalletResetModeValues = typeof EnergyWalletResetModes[keyof typeof EnergyWalletResetModes];

export const EnergyWalletSourceTypes = {
  USER_PURCHASE: 'USER_PURCHASE',
  COMPANY_GRANT: 'COMPANY_GRANT',
  CONTRACT_GRANT: 'CONTRACT_GRANT',
  PROMO: 'PROMO',
  ADMIN_ADJUSTMENT: 'ADMIN_ADJUSTMENT',
  REFUND: 'REFUND',
  MIGRATION: 'MIGRATION',
} as const;
export type EnergyWalletSourceTypeValues = typeof EnergyWalletSourceTypes[keyof typeof EnergyWalletSourceTypes];

export const EnergyWalletPayerTypes = {
  USER: 'USER',
  COMPANY: 'COMPANY',
  SYSTEM: 'SYSTEM',
} as const;
export type EnergyWalletPayerTypeValues = typeof EnergyWalletPayerTypes[keyof typeof EnergyWalletPayerTypes];

export const EnergyWalletCreditLotStatuses = {
  ACTIVE: 'active',
  EXHAUSTED: 'exhausted',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
} as const;
export type EnergyWalletCreditLotStatusValues = typeof EnergyWalletCreditLotStatuses[keyof typeof EnergyWalletCreditLotStatuses];

export const EnergyWalletMovementTypes = {
  LOAD: 'LOAD',
  UNLOAD: 'UNLOAD',
  HOLD: 'HOLD',
  RELEASE: 'RELEASE',
  REVERSAL: 'REVERSAL',
  EXPIRE: 'EXPIRE',
  ADJUSTMENT: 'ADJUSTMENT',
} as const;
export type EnergyWalletMovementTypeValues = typeof EnergyWalletMovementTypes[keyof typeof EnergyWalletMovementTypes];

export const EnergyWalletMovementStatuses = {
  PENDING: 'pending',
  POSTED: 'posted',
  CANCELLED: 'cancelled',
} as const;
export type EnergyWalletMovementStatusValues = typeof EnergyWalletMovementStatuses[keyof typeof EnergyWalletMovementStatuses];

export const EnergyWalletHoldStatuses = {
  ACTIVE: 'active',
  RELEASED: 'released',
  CONSUMED: 'consumed',
  EXPIRED: 'expired',
} as const;
export type EnergyWalletHoldStatusValues = typeof EnergyWalletHoldStatuses[keyof typeof EnergyWalletHoldStatuses];

export interface IEnergyWallet {
  id: number;
  userId: string;
  user?: IUser;
  status: EnergyWalletStatusValues;
  balanceWh: number;
  reservedWh: number;
  metadata?: IJsonObject;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface IEnergyWalletPolicy {
  id: number;
  name: string;
  status: EnergyWalletPolicyStatusValues;
  grantMode: EnergyWalletGrantModeValues;
  defaultLoadWh?: number;
  periodType: EnergyWalletPeriodTypeValues;
  resetMode: EnergyWalletResetModeValues;
  companyId?: number;
  company?: IEnergyWalletCompanySummary;
  contractId?: number;
  contractVersionId?: number;
  contractVersionScopeId?: number;
  appliesToGroupId?: number;
  validFrom?: Date;
  validTo?: Date;
  notes?: string;
  metadata?: IJsonObject;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface IEnergyWalletCreditLot {
  id: number;
  walletId: number;
  userId: string;
  policyId?: number;
  sourceType: EnergyWalletSourceTypeValues;
  payerType: EnergyWalletPayerTypeValues;
  payerUserId?: string;
  payerCompanyId?: number;
  payerCompany?: IEnergyWalletCompanySummary;
  contractId?: number;
  contractVersionId?: number;
  contractVersionScopeId?: number;
  loadedWh: number;
  remainingWh: number;
  paidAmount?: number;
  currency: string;
  status: EnergyWalletCreditLotStatusValues;
  reason: string;
  externalReference?: string;
  expiresAt?: Date;
  metadata?: IJsonObject;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface IEnergyWalletMovement {
  id: number;
  walletId: number;
  creditLotId?: number;
  policyId?: number;
  userId: string;
  sessionId?: string;
  cdrId?: string;
  type: EnergyWalletMovementTypeValues;
  status: EnergyWalletMovementStatusValues;
  quantityWh: number;
  balanceAfterWh: number;
  coveredAmount?: number;
  currency: string;
  sourceType?: EnergyWalletSourceTypeValues;
  payerType?: EnergyWalletPayerTypeValues;
  payerUserId?: string;
  payerCompanyId?: number;
  payerCompany?: IEnergyWalletCompanySummary;
  contractId?: number;
  contractVersionId?: number;
  contractVersionScopeId?: number;
  idempotencyKey: string;
  reason: string;
  reversedMovementId?: number;
  metadata?: IJsonObject;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface IEnergyWalletHold {
  id: number;
  walletId: number;
  userId: string;
  sessionId: string;
  reservedWh: number;
  consumedWh: number;
  status: EnergyWalletHoldStatusValues;
  expiresAt?: Date;
  metadata?: IJsonObject;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ICoverageRuleSummary {
  id?: number;
  sponsorshipPolicyId?: number | null;
  componentType?: CoverageComponentTypeValues | null;
  coverageType?: CoverageTypeValues | null;
  value?: number | null;
  unit?: CoverageUnitValues | null;
  priority?: number | null;
  notes?: string | null;
}

export interface ICoverageRuleLimitSummary {
  id?: number;
  sponsorshipPolicyId?: number | null;
  limitScope?: CoverageLimitScopeValues | null;
  period?: CoverageLimitPeriodValues | null;
  metric?: CoverageLimitMetricValues | null;
  limitValue?: number | null;
  behaviorOnExceed?: CoverageLimitBehaviorOnExceedValues | null;
  notes?: string | null;
}

export interface ISponsorshipActivationRuleSummary {
  id?: number;
  sponsorshipPolicyId?: number | null;
  startDay?: SponsorshipScheduleDayValues | string | null;
  startTime?: string | null;
  endDay?: SponsorshipScheduleDayValues | string | null;
  endTime?: string | null;
  timezone?: SponsorshipActivationRuleTimezoneValues | string | null;
  notes?: string | null;
}

export interface IEnergyWalletSponsorshipSummary {
  policyId?: number;
  policyName?: string;
  companyId?: number;
  companyName?: string;
  appliesToCurrentUser?: boolean;
  appliesToGroupId?: number;
  appliesToGroupName?: string;
  activationRules?: ISponsorshipActivationRuleSummary[];
  coverageRules?: ICoverageRuleSummary[];
  coverageRuleLimits?: ICoverageRuleLimitSummary[];
}

export interface IEnergyWalletBalanceDTO {
  wallet: IEnergyWallet;
  availableWh: number;
  availableKwh: number;
  activeLots: IEnergyWalletCreditLot[];
  recentMovements: IEnergyWalletMovement[];
  activePolicies?: IEnergyWalletPolicy[];
  sponsorship?: IEnergyWalletSponsorshipSummary | null;
}

export interface ICreateEnergyWalletLoadDTO {
  userId: string;
  quantityWh: number;
  sourceType: EnergyWalletSourceTypeValues;
  payerType: EnergyWalletPayerTypeValues;
  payerUserId?: string;
  payerCompanyId?: number;
  policyId?: number;
  contractId?: number;
  contractVersionId?: number;
  contractVersionScopeId?: number;
  paidAmount?: number;
  currency?: string;
  reason: string;
  externalReference?: string;
  expiresAt?: Date;
  idempotencyKey?: string;
  metadata?: IJsonObject;
}

export interface ICreateEnergyWalletAdjustmentDTO {
  userId: string;
  quantityWh: number;
  reason: string;
  idempotencyKey?: string;
  metadata?: IJsonObject;
}

export interface IEnergyWalletConsumeResultDTO {
  walletId?: number;
  consumedWh: number;
  coveredAmount: number;
  currency: string;
  movementIds: number[];
  skippedReason?: string;
}

export interface IConnectorSponsorshipBenefitDTO {
  policyId?: number | null;
  policyName?: string | null;
  companyId?: number | null;
  priority?: number | null;
  primary?: boolean;
  applied: boolean;
  userInDomain: boolean;
  companyCoveredAtReference: boolean;
  referenceDateTime?: Date | string | null;
  referenceDateSource?: string | null;
  retailTariffId?: string | null;
  coverageRules: ICoverageRuleSummary[];
  coverageRuleLimits: ICoverageRuleLimitSummary[];
}

export interface IConnectorChargingBenefitsDTO {
  connectorId: number;
  sponsorship?: IConnectorSponsorshipBenefitDTO | null;
  sponsorships?: IConnectorSponsorshipBenefitDTO[];
}
